import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, ShoppingBag, Home, Bell, XCircle } from 'lucide-react';
import LoadingSpinner from '../components/common/LoadingSpinner';
import EmptyStateWithRedirect from '../components/common/EmptyStateWithRedirect';
import { pickUserSubscriptionsEmpty } from '../utils/emptyStates';
import { MarketplaceSubscription } from '../types/marketplace';
import marketplaceService from '../services/marketplaceService';
import SubscriptionCard from '../components/marketplace/SubscriptionCard';
import DailyInstructions from '../components/marketplace/DailyInstructions';
import MarketplaceFAQ from '../components/marketplace/MarketplaceFAQ';
import StandardLayout from '../components/common/StandardLayout';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { isWhiteLabel } from '../config/tenantConfig';

const UserSubscriptionsPage: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<MarketplaceSubscription[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscriptions = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await marketplaceService.getSubscriptions();
      setSubscriptions(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load subscriptions');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const activeCount = subscriptions.filter((s) => s.status === 'ACTIVE').length;
  const pausedCount = subscriptions.filter((s) => s.status === 'PAUSED').length;

  return (
    <StandardLayout>
      <PageHeader
        title="My Subscriptions"
        pageType="management"
        icon={<Bell className="w-5 h-5" />}
        breadcrumbs={[
          { label: 'Home', href: '/', icon: <Home className="w-4 h-4" /> },
          { label: 'Marketplace' },
        ]}
        actions={
          <Button
            variant="outline"
            onClick={fetchSubscriptions}
            leftIcon={<RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />}
            className="w-full sm:w-auto"
            disabled={isLoading}
          >
            Refresh
          </Button>
        }
      />

      {/* Daily operating instructions — persistent, collapsible. */}
      <DailyInstructions variant="banner" />

      {/* Stats */}
      {!isLoading && subscriptions.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Total</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">{subscriptions.length}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Active</p>
            <p className={`text-xl font-bold mt-1 ${isWhiteLabel() ? 'text-zebu-600 dark:text-zebu-400' : 'text-blue-600 dark:text-blue-400'}`}>{activeCount}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Paused</p>
            <p className="text-xl font-bold text-yellow-600 mt-1">{pausedCount}</p>
          </div>
        </div>
      )}

      {/* Loading */}
      {isLoading && <LoadingSpinner message="Loading subscriptions..." />}

      {/* Error */}
      {error && !isLoading && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
            <span className="text-red-800 dark:text-red-200 text-sm">{error}</span>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && subscriptions.length === 0 && (
        <Card>
          <CardContent>
            <EmptyStateWithRedirect
              icon={<ShoppingBag className="w-10 h-10" />}
              {...pickUserSubscriptionsEmpty()}
            />
          </CardContent>
        </Card>
      )}

      {/* Subscription Grid */}
      {!isLoading && subscriptions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Subscriptions ({subscriptions.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {subscriptions.map((sub) => (
                <SubscriptionCard
                  key={sub.subscription_id}
                  subscription={sub}
                  onUpdate={fetchSubscriptions}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* FAQ — subscription behaviour (pause / unsubscribe / daily login). */}
      {!isLoading && <MarketplaceFAQ />}
    </StandardLayout>
  );
};

export default UserSubscriptionsPage;
